import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ChevronRight, Info, Plane, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Money } from '../../contexts/CurrencyContext';
import { AirlineLogo } from '../../utils/airlineLogos';
import { BfmFlightCard, hhmm, shortDate } from '../flight/BfmFlightCard';
import { paxCount, perPerson, optionsForStep, finalItinerary } from './multiCityLogic';

const SectorLabel = ({ leg, index }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`mc-search-sector-${index}`}>
    <span className="font-bold">Sector {index + 1}</span><span>{leg.origin} → {leg.destination}</span><span className="opacity-80">· {shortDate(leg.departure_date)}</span>
  </span>
);

const SearchBar = ({ legs, passengers, count, onModifySearch }) => (
  <div className="bg-[#FF6600] text-white rounded-2xl shadow-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="mc-search-bar">
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">{legs.map((l, i) => <SectorLabel key={i} leg={l} index={i} />)}</div>
      <div className="text-xs text-white/90 mt-1" data-testid="mc-search-meta">{paxCount(passengers)} passenger{paxCount(passengers) !== 1 ? 's' : ''} · Multi-city · {count} Sabre-priced itinerar{count === 1 ? 'y' : 'ies'}</div>
    </div>
    <Button variant="secondary" onClick={onModifySearch} className="bg-white text-[#FF6600] hover:bg-orange-50 font-semibold h-9 rounded-full" data-testid="mc-modify-search-btn">Modify search</Button>
  </div>
);

const LegRow = ({ leg, muted }) => (
  <div className={`flex items-center gap-3 ${muted ? 'opacity-60' : ''}`}>
    <AirlineLogo code={leg.airline_code} className="w-8 h-8" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-[#222222] truncate">{leg.from} → {leg.to} · {leg.airline_name || leg.airline} {leg.flight_number}</div>
      <div className="text-xs text-slate-500">{shortDate(leg.departure_date)} · {hhmm(leg.departure_time)} – {hhmm(leg.arrival_time)} · {leg.duration} · {leg.stops === 0 ? 'Direct' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}`}</div>
    </div>
  </div>
);

const SelectedSummary = ({ legs, selectedKeys, itin, pax, onChange, complete }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="mc-selected-summary">
    <div className="flex items-center justify-between gap-3 mb-3">
      <h2 className="text-base md:text-lg font-bold text-[#222222]">Your itinerary</h2>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Sabre BFM priced</span>
    </div>
    <div className="space-y-2">
      {legs.map((l, i) => {
        const chosen = i < selectedKeys.length;
        const leg = itin?.legs?.[i];
        const current = i === selectedKeys.length && !complete;
        return (
          <div key={i} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${current ? 'border-[#FF6600] bg-orange-50/50' : 'border-slate-100 bg-slate-50'}`} data-testid={`mc-summary-sector-${i}`}>
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${chosen || complete ? 'bg-[#FF6600] text-white' : current ? 'bg-[#222222] text-white' : 'bg-slate-200 text-slate-600'}`}>{chosen || complete ? <Check className="h-4 w-4" /> : i + 1}</span>
            <div className="flex-1 min-w-0">
              {leg && (chosen || complete) ? <LegRow leg={leg} /> : leg ? <LegRow leg={leg} muted /> : <div className="text-sm text-slate-600">{l.origin} → {l.destination} · {shortDate(l.departure_date)}</div>}
              {!chosen && !complete && <div className="text-[11px] text-slate-500 mt-0.5">{current ? 'Choose below' : leg ? 'Recommended — confirm after the sectors above' : 'Pending'}</div>}
            </div>
            {(chosen || complete) && <button type="button" onClick={() => onChange(i)} className="text-xs font-semibold text-[#FF6600] hover:underline shrink-0" data-testid={`mc-change-sector-${i}`}>Change</button>}
          </div>
        );
      })}
    </div>
    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-end justify-between gap-2">
      <div className="text-xs text-slate-500 flex items-center gap-1"><Info className="h-3.5 w-3.5" />{complete ? 'Complete itinerary fare for all sectors' : itin ? 'Whole-trip fare with the recommended combination' : 'Pick a flight to see the full fare'}</div>
      {itin ? (
        <div className="text-right">
          <div className="text-2xl font-black text-[#FF6600]" data-testid="mc-total-pp"><Money gbp={perPerson(itin, pax)} /> <span className="text-sm font-semibold text-slate-500">per person</span></div>
          <div className="text-xs text-slate-600" data-testid="mc-total-all"><Money gbp={Math.round(itin.price)} /> total for {paxCount(pax)} passenger{paxCount(pax) !== 1 ? 's' : ''}</div>
        </div>
      ) : null}
    </div>
  </div>
);

const RecommendedPanel = ({ itin, step, pax, onAccept, disabled }) => (
  <div className="rounded-2xl border-2 border-[#FF6600] bg-orange-50/60 p-4" data-testid="mc-recommended-panel">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#FF6600]">Recommended · complete BFM itinerary</div>
        <div className="text-sm text-slate-700">{itin.same_airline ? `${itin.airline_name} on every sector` : 'Best-priced combination for your selection'} — one fare for the whole trip</div>
      </div>
      <div className="text-right">
        <div className="text-xl font-black text-[#222222]" data-testid="mc-recommended-pp"><Money gbp={perPerson(itin, pax)} /> <span className="text-xs font-semibold text-slate-500">pp</span></div>
        <div className="text-[11px] text-slate-500"><Money gbp={Math.round(itin.price)} /> for {paxCount(pax)} pax</div>
      </div>
    </div>
    <div className="space-y-2">{itin.legs.slice(step).map((leg, i) => <div key={leg.key} className="bg-white rounded-xl border border-orange-100 px-3 py-2" data-testid={`mc-recommended-leg-${step + i}`}><LegRow leg={leg} /></div>)}</div>
    <Button onClick={onAccept} disabled={disabled} className="mt-3 w-full sm:w-auto bg-[#FF6600] hover:bg-[#E65C00] text-white font-bold rounded-full" data-testid="mc-accept-recommended-btn">Select this combination <ChevronRight className="h-4 w-4 ml-1" /></Button>
  </div>
);

const OptionCard = ({ opt, step, pax, recommended, onChoose, index, disabled }) => {
  const n = paxCount(pax);
  const badges = [];
  if (opt.isRecommended) badges.push({ text: 'Recommended', tone: 'orange', testId: `mc-option-${step}-${index}-recommended` });
  else if (opt.saving > 0) badges.push({ text: `Save £${Math.round(opt.saving / n)} pp`, tone: 'green' });
  if (!opt.primary.same_airline) badges.push({ text: 'Mixed airlines', tone: 'dark' });
  const cheaperMixed = opt.cheapest.price < opt.primary.price;
  const dearer = recommended && !opt.isRecommended && opt.saving < 0 ? Math.round(-opt.saving / n) : 0;
  return (
    <BfmFlightCard
      leg={opt.leg}
      itin={opt.primary}
      price={perPerson(opt.primary, pax)}
      priceCaption={`per person · whole trip${dearer ? ` · +£${dearer} pp` : ''}`}
      priceNote={cheaperMixed ? <span>Mixed-airline combos from <Money gbp={perPerson(opt.cheapest, pax)} /> pp</span> : (opt.count > 1 ? `${opt.count} BFM fares for this flight` : null)}
      badges={badges}
      highlight={opt.isRecommended}
      onSelect={() => onChoose(opt.key)}
      selectLabel={disabled ? 'Checking…' : 'Select'}
      disabled={disabled}
      testId={`mc-option-${step}-${index}`}
    />
  );
};

export const MultiCityResults = ({ legs = [], itineraries = [], passengers = {}, onSelectFlight, onModifySearch, isRevalidating = false, note }) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => { setSelectedKeys([]); }, [itineraries]);
  const legCount = legs.length;
  const step = Math.min(selectedKeys.length, Math.max(legCount - 1, 0));
  const { options, recommended } = useMemo(() => optionsForStep(itineraries, selectedKeys, step), [itineraries, selectedKeys, step]);
  const complete = useMemo(() => finalItinerary(itineraries, selectedKeys, legCount), [itineraries, selectedKeys, legCount]);
  const recommendedOpt = options.find((o) => o.isRecommended);
  const others = options.filter((o) => !o.isRecommended);
  const choose = (key) => { setSelectedKeys((k) => [...k, key]); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  if (!itineraries.length) {
    return (
      <div className="container mx-auto px-2 sm:px-4 py-4 space-y-4" data-testid="mc-results">
        <SearchBar legs={legs} passengers={passengers} count={0} onModifySearch={onModifySearch} />
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="mc-no-results">
          <Plane className="h-8 w-8 mx-auto text-[#FF6600] mb-2" />
          <p className="font-semibold text-[#222222]">No complete Sabre-priced itinerary covers all your sectors</p>
          <p className="text-sm text-slate-500 mt-1">{note || 'Try different dates or nearby airports. We only show combinations Sabre can price and book as one itinerary.'}</p>
          <Button onClick={onModifySearch} variant="outline" className="mt-4 rounded-full"><ArrowLeft className="h-4 w-4 mr-2" />Modify search</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 space-y-4" data-testid="mc-results">
      <SearchBar legs={legs} passengers={passengers} count={itineraries.length} onModifySearch={onModifySearch} />
      <SelectedSummary legs={legs} selectedKeys={selectedKeys} itin={complete || (step > 0 ? recommended : null)} pax={passengers} complete={!!complete} onChange={(i) => setSelectedKeys((k) => k.slice(0, i))} />

      {complete ? (
        <>
          <div className="bg-white rounded-2xl border-2 border-emerald-300 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="mc-complete-panel">
            <div>
              <div className="font-bold text-[#222222]">All sectors selected — one Sabre itinerary, one fare</div>
              <div className="text-sm text-slate-600">We re-check this exact combination with Sabre before passenger details and again before payment.</div>
            </div>
            <Button onClick={() => onSelectFlight(complete)} disabled={isRevalidating} className="bg-[#FF6600] hover:bg-[#E65C00] text-white font-bold h-12 px-6 rounded-full" data-testid="mc-continue-btn">
              {isRevalidating ? 'Checking with Sabre…' : <>Continue · <Money gbp={perPerson(complete, passengers)} /> pp</>}
            </Button>
          </div>
          <div className="space-y-3" data-testid="mc-final-legs">
            {complete.legs.map((leg, i) => <BfmFlightCard key={leg.key} leg={leg} itin={complete} price={perPerson(complete, passengers)} priceCaption="per person · whole trip" badges={[{ text: `Sector ${i + 1}`, tone: 'dark' }]} testId={`mc-final-leg-${i}`} />)}
          </div>
        </>
      ) : (
        <>
          {step > 0 && recommended && <RecommendedPanel itin={recommended} step={step} pax={passengers} disabled={isRevalidating} onAccept={() => setSelectedKeys(recommended.legs.map((l) => l.key))} />}
          <div className="flex items-center justify-between gap-2 pt-2">
            <h3 className="text-base md:text-lg font-bold text-[#222222]" data-testid="mc-step-title">{step > 0 ? 'Or choose a different' : 'Choose your'} Sector {step + 1} flight · {legs[step].origin} → {legs[step].destination} · {shortDate(legs[step].departure_date)}</h3>
            <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="mc-step-count">{options.length} option{options.length !== 1 ? 's' : ''}</span>
          </div>
          <p className="text-xs text-slate-500 -mt-2 flex items-center gap-1"><Info className="h-3.5 w-3.5" />Every price is the complete {legCount}-sector itinerary per person, exactly as priced by Sabre — never sectors added together.</p>
          {step === 0 && recommendedOpt && <OptionCard opt={recommendedOpt} step={step} pax={passengers} recommended={recommended} onChoose={choose} index={0} disabled={isRevalidating} />}
          {others.length > 0 && (
            <>
              <div className="text-sm font-bold text-slate-700 pt-1" data-testid="mc-others-title">{step === 0 ? 'More Sabre-priced flights' : 'Other valid combinations'} · cheapest first</div>
              {others.map((o, i) => <OptionCard key={o.key} opt={o} step={step} pax={passengers} recommended={recommended} onChoose={choose} index={i + 1} disabled={isRevalidating} />)}
            </>
          )}
        </>
      )}
    </div>
  );
};
